import { type GraphQLQuery } from '@affine/graphql';
import { Store } from '@toeverything/infra';

import type { GraphQLService } from '../services/graphql';

export interface LlmModel {
  id: string;
  modelId: string;
  displayName: string;
  isDefault: boolean;
  enabled: boolean;
}

export interface LlmProvider {
  id: string;
  name: string;
  kind: string;
  baseUrl: string;
  enabled: boolean;
  keyMasked: string | null;
  models: LlmModel[];
}

export interface UpsertLlmProviderInput {
  id?: string;
  name: string;
  kind: string;
  baseUrl: string;
  apiKey?: string;
}

export interface UpsertLlmModelInput {
  id?: string;
  providerId: string;
  modelId: string;
  displayName: string;
}

const providerSelection = `id name kind baseUrl enabled keyMasked models { id modelId displayName isDefault enabled }`;
const modelSelection = `id modelId displayName isDefault enabled`;

const llmProvidersQuery: GraphQLQuery = {
  id: 'llmProvidersQuery',
  op: 'llmProviders',
  query: `query llmProviders($workspaceId: String!) {
    llmProviders(workspaceId: $workspaceId) { ${providerSelection} }
  }`,
};

const upsertLlmProviderMutation: GraphQLQuery = {
  id: 'upsertLlmProviderMutation',
  op: 'upsertLlmProvider',
  query: `mutation upsertLlmProvider($workspaceId: String!, $input: UpsertLlmProviderInput!) {
    upsertLlmProvider(workspaceId: $workspaceId, input: $input) { ${providerSelection} }
  }`,
};

const deleteLlmProviderMutation: GraphQLQuery = {
  id: 'deleteLlmProviderMutation',
  op: 'deleteLlmProvider',
  query: `mutation deleteLlmProvider($workspaceId: String!, $id: String!) {
    deleteLlmProvider(workspaceId: $workspaceId, id: $id)
  }`,
};

const upsertLlmModelMutation: GraphQLQuery = {
  id: 'upsertLlmModelMutation',
  op: 'upsertLlmModel',
  query: `mutation upsertLlmModel($workspaceId: String!, $input: UpsertLlmModelInput!) {
    upsertLlmModel(workspaceId: $workspaceId, input: $input) { ${modelSelection} }
  }`,
};

const setLlmModelEnabledMutation: GraphQLQuery = {
  id: 'setLlmModelEnabledMutation',
  op: 'setLlmModelEnabled',
  query: `mutation setLlmModelEnabled($workspaceId: String!, $id: String!, $enabled: Boolean!) {
    setLlmModelEnabled(workspaceId: $workspaceId, id: $id, enabled: $enabled)
  }`,
};

const setDefaultLlmModelMutation: GraphQLQuery = {
  id: 'setDefaultLlmModelMutation',
  op: 'setDefaultLlmModel',
  query: `mutation setDefaultLlmModel($workspaceId: String!, $modelId: String!) {
    setDefaultLlmModel(workspaceId: $workspaceId, modelId: $modelId)
  }`,
};

const deleteLlmModelMutation: GraphQLQuery = {
  id: 'deleteLlmModelMutation',
  op: 'deleteLlmModel',
  query: `mutation deleteLlmModel($workspaceId: String!, $id: String!) {
    deleteLlmModel(workspaceId: $workspaceId, id: $id)
  }`,
};

export class LlmProviderStore extends Store {
  constructor(private readonly gqlService: GraphQLService) {
    super();
  }

  // The LLM provider/model GraphQL operations are PIPE-custom and not part of the generated
  // @affine/graphql types, so they are issued untyped and the known response shape is asserted
  // at this single boundary.
  private async exec<T>(
    query: unknown,
    variables: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<T> {
    return (await this.gqlService.gql({
      query,
      variables,
      context: { signal },
    } as any)) as unknown as T;
  }

  async listProviders(
    workspaceId: string,
    signal?: AbortSignal
  ): Promise<LlmProvider[]> {
    const data = await this.exec<{ llmProviders: LlmProvider[] }>(
      llmProvidersQuery,
      { workspaceId },
      signal
    );

    return data.llmProviders;
  }

  async upsertProvider(
    workspaceId: string,
    input: UpsertLlmProviderInput,
    signal?: AbortSignal
  ): Promise<LlmProvider> {
    const data = await this.exec<{ upsertLlmProvider: LlmProvider }>(
      upsertLlmProviderMutation,
      { workspaceId, input },
      signal
    );

    return data.upsertLlmProvider;
  }

  async deleteProvider(
    workspaceId: string,
    id: string,
    signal?: AbortSignal
  ): Promise<boolean> {
    const data = await this.exec<{ deleteLlmProvider: boolean }>(
      deleteLlmProviderMutation,
      { workspaceId, id },
      signal
    );

    return data.deleteLlmProvider;
  }

  async upsertModel(
    workspaceId: string,
    input: UpsertLlmModelInput,
    signal?: AbortSignal
  ): Promise<LlmModel> {
    const data = await this.exec<{ upsertLlmModel: LlmModel }>(
      upsertLlmModelMutation,
      { workspaceId, input },
      signal
    );

    return data.upsertLlmModel;
  }

  async setModelEnabled(
    workspaceId: string,
    id: string,
    enabled: boolean,
    signal?: AbortSignal
  ): Promise<boolean> {
    const data = await this.exec<{ setLlmModelEnabled: boolean }>(
      setLlmModelEnabledMutation,
      { workspaceId, id, enabled },
      signal
    );

    return data.setLlmModelEnabled;
  }

  async setDefaultModel(
    workspaceId: string,
    modelId: string,
    signal?: AbortSignal
  ): Promise<boolean> {
    const data = await this.exec<{ setDefaultLlmModel: boolean }>(
      setDefaultLlmModelMutation,
      { workspaceId, modelId },
      signal
    );

    return data.setDefaultLlmModel;
  }

  async deleteModel(
    workspaceId: string,
    id: string,
    signal?: AbortSignal
  ): Promise<boolean> {
    const data = await this.exec<{ deleteLlmModel: boolean }>(
      deleteLlmModelMutation,
      { workspaceId, id },
      signal
    );

    return data.deleteLlmModel;
  }
}
